import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import { db } from "../../../../firebase";
import CourseForm from "../../../../Components/Form/CourseForm";
import { courseListingFields } from "../../../../formSchemas/courseFormSchemas";
import { showSuccessToast, showErrorToast } from "../../../../toastmsg";
import { slugify } from "../../../../utils/slug";

// Map Firestore data to form, ensure objectList fields are arrays
const mapFirestoreToForm = (data: any) => ({
  courseName: data.courseName || "-",
  universityName: data.universityName || "-",
  duration: data.duration || "-",
  intake: data.intake || [],
  tuitionFee: Number(data.tuitionFee ?? data.totalFee ?? 0),
  registrationFee: Number(data.registrationFee ?? 0),
  resourceFee: Number(data.resourceFee ?? 0),
  extraFees: Array.isArray(data.extraFees) ? data.extraFees : [],
  entryDetails: Array.isArray(data.entryDetails) ? data.entryDetails : [],
  interview: data.interview || "No",
  englishRequirements: Array.isArray(data.englishRequirements)
    ? data.englishRequirements
    : [],
  deals: data.deals || [],
  scholarships: data.scholarships || [],
  fieldOfStudy: data.fieldOfStudy,
  major: data.major,
  level: data.level,
  paymentSchemes: data.paymentSchemes,
  partTimeOption: data.partTimeOption,
  abroadDuration: data.abroadDuration,
  country: data.country,
  state: data.state,
  overseasTransfer: data.overseasTransfer,
  // ✅ normalize entryRequirements for ObjectListEditor
  entryRequirements: Array.isArray(data.entryRequirements)
    ? data.entryRequirements
    : [],
});

const mapFormToFirestore = (data: any) => ({
  courseName: data.courseName || "-",
  universityName: data.universityName || "-",
  duration: data.duration || "-",
  intake: data.intake || [],
  tuitionFee: Number(data.tuitionFee) || 0,
  registrationFee: Number(data.registrationFee) || 0,
  resourceFee: Number(data.resourceFee) || 0,
  extraFees: Array.isArray(data.extraFees) ? data.extraFees : [],
  entryDetails: Array.isArray(data.entryDetails) ? data.entryDetails : [],
  interview: data.interview || "No",
  englishRequirements: Array.isArray(data.englishRequirements)
    ? data.englishRequirements
    : [],
  deals: data.deals || [],
  scholarships: data.scholarships || [],
  fieldOfStudy: data.fieldOfStudy,
  major: data.major,
  level: data.level,
  paymentSchemes: data.paymentSchemes,
  partTimeOption: data.partTimeOption,
  abroadDuration: data.abroadDuration,
  country: data.country,
  state: data.state,
  overseasTransfer: data.overseasTransfer,
  entryRequirements: Array.isArray(data.entryRequirements)
    ? data.entryRequirements
    : [],
});

export default function EditCourse() {
  const { id } = useParams();
  const [initialData, setInitialData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [universityNames, setUniversityNames] = useState<string[]>([]);
  const [courseFields, setCourseFields] = useState(courseListingFields);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        // Fetch course data
        const docRef = doc(db, "course-listing", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setInitialData(mapFirestoreToForm(docSnap.data()));
        }

        // Fetch university names
        const querySnapshot = await getDocs(
          collection(db, "university-listing")
        );
        const names = querySnapshot.docs
          .map((doc) => doc.data().universityName)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        setUniversityNames(names);

        // Update course fields with dynamic university options
        const updatedFields = courseListingFields.map((field) =>
          field.name === "universityName" ? { ...field, options: names } : field
        );
        setCourseFields(updatedFields);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async (formData: any) => {
    if (!id) return;
    try {
      const scholarshipsArray = (formData.scholarships || []).map((s: any) => ({
        title: s.title,
        amount: Number(s.amount) || 0,
      }));

      // ✅ Only include valid fields defined in courseListingFields
      const allowedKeys = courseListingFields.map((f) => f.name);

      const filteredFormData = Object.keys(formData)
        .filter((key) => allowedKeys.includes(key))
        .reduce((obj: any, key) => {
          obj[key] = formData[key];
          return obj;
        }, {});

      const firestoreData = {
        ...mapFormToFirestore(filteredFormData),
        scholarships: scholarshipsArray,
        courseSlug: slugify(
          `${formData.universityName} ${formData.courseName}`
        ),
        uniSlug: slugify(`${formData.universityName}`),
        majorSlug: slugify(`${formData.major}`),
        fieldOfStudySlug: slugify(`${formData.fieldOfStudy}`),
      };

      await updateDoc(doc(db, "course-listing", id), firestoreData);
      showSuccessToast("Course updated successfully");
      navigate("/course-listing");
    } catch (error: any) {
      console.error("Error updating course:", error);
      showErrorToast(error?.message || "Failed to update course");
    }
  };

  const handleCancel = () => {
    navigate("/course-listing");
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Edit Course</h2>
      <CourseForm
        fields={courseFields}
        initialData={initialData}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </div>
  );
}
